import { FormModal } from '~/shared/forms/FormModal';
import type { FormValues } from '~/shared/forms/types';
import {
	editCustomInterventionOptions,
	InterventionDefinitionTypeEnum,
	type EditCustomInterventionCommand,
	type FullOrderViewModel,
	type InterventionViewModel
} from '@generated';
import { getCustomInterventionValidationSchema } from './util';
import { CustomInterventionFormFields } from './CustomInterventionFormFields';
import { useGoalsAndnterventionsForOrder } from '../queries';
import { useSuspenseQuery } from '@tanstack/react-query';

export function getCustomInterventionInitialValues(intervention: InterventionViewModel) {
	return {
		subject: intervention.customInterventionSubject,
		definitionText: intervention.customInterventionDefinitionText,
		interventionTypeId: intervention.customInterventionTypeId,
		canBePerformedByPatient: intervention.customCanBePerformedByPatient,
		canBePerformedByCaregiver: intervention.customCanBePerformedByCaregiver,
		projectedStartDate: intervention.projectedStartDate,
		projectedEndDate: intervention.projectedEndDate,
		notes: intervention.notes,
		assignedDisciplineId: intervention.customAssignedDisciplineId
	};
}

export function EditCustomInterventionFormModal({
	interventionId,
	fullOrder,
	closeModal
}: {
	interventionId: string;
	fullOrder: FullOrderViewModel;
	closeModal: () => void;
}) {
	const intervention = fullOrder.interventions.find(intervention => intervention.id === interventionId);
	if (!intervention) {
		throw new Error('Draft intervention not found');
	} else if (intervention.interventionDefinitionTypeId !== InterventionDefinitionTypeEnum.Custom) {
		throw new Error('Draft intervention is not a custom intervention');
	}
	const { refetch } = useSuspenseQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const initialValues = getCustomInterventionInitialValues(intervention);
	const validationSchema = getCustomInterventionValidationSchema(fullOrder.certificationPeriod.endDate);

	const onSaveTransform = (values: FormValues): EditCustomInterventionCommand => {
		return {
			orderId: fullOrder.id,
			interventionId: intervention.id,
			notes: values?.notes,
			projectedStartDate: values.projectedStartDate,
			projectedEndDate: values.projectedEndDate,
			assignedDisciplineId: values.assignedDisciplineId,
			canBePerformedByCaregiver: values.canBePerformedByCaregiver,
			canBePerformedByPatient: values.canBePerformedByPatient,
			interventionTypeId: values.interventionTypeId,
			subject: values.subject,
			definitionText: values.definitionText
		};
	};

	return (
		<FormModal
			title="Edit Intervention"
			closeModal={closeModal}
			initialValues={initialValues}
			saveMutation={editCustomInterventionOptions}
			onSuccess={() => {
				refetch();
				closeModal();
			}}
			onSaveTransform={onSaveTransform}
			validationSchema={validationSchema}
			size="lg"
		>
			<CustomInterventionFormFields />
		</FormModal>
	);
}
